<script context="module">
    // A module-scoped counter, NOT Math.random(). Under SSR a random id differs
    // between the server render and the client hydration, which breaks
    // <label for> and produces a hydration mismatch. A counter increments in
    // the same component order on both sides, so the ids line up. This matters
    // the day the app moves to SvelteKit.
    let uid = 0;
</script>

<script>
    // Wraps one form control with its label, hint and error, and wires the
    // aria-* relationships that make the error reachable by a screen reader.
    //
    //   <Field label="Your age" hint="Used to pick retinoid strength" let:id>
    //     <input {id} type="number" bind:value={age} />
    //   </Field>
    //
    //   <Field label="Email" error={emailError} required let:id let:describedBy>
    //     <input {id} aria-describedby={describedBy} bind:value={email} />
    //   </Field>

    export let label = null;
    export let hint = null;
    export let error = null;
    export let required = false;
    export let id = null;

    const fallbackId = `sg-field-${++uid}`;

    $: resolvedId = id || fallbackId;
    $: hintId = hint && !error ? `${resolvedId}-hint` : null;
    $: errorId = error ? `${resolvedId}-error` : null;
    $: describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
</script>

<style>
    .field { margin-bottom: var(--space-5); }

    .label {
        display: block;
        font-size: var(--text-2xs);
        font-weight: var(--weight-medium);
        letter-spacing: var(--track-label);
        text-transform: uppercase;
        color: var(--text-muted);
        margin-bottom: var(--space-2);
    }

    .required { color: var(--rose); margin-left: var(--space-1); }

    .hint {
        display: block;
        font-size: var(--text-xs);
        color: var(--text-muted);
        margin-top: var(--space-2);
    }

    .error {
        display: block;
        font-size: var(--text-xs);
        color: var(--danger);
        margin-top: var(--space-2);
    }

    /* Reach the control the caller passed into the slot. */
    .control :global(input),
    .control :global(select),
    .control :global(textarea) {
        width: 100%;
        margin-bottom: 0;
    }

    .has-error :global(input),
    .has-error :global(select),
    .has-error :global(textarea) {
        border-color: var(--danger);
    }
</style>

<div class="field">
    {#if label}
        <label class="label" for={resolvedId}>
            {label}{#if required}<span class="required" aria-hidden="true">*</span><span class="u-visually-hidden">(required)</span>{/if}
        </label>
    {/if}

    <div class="control" class:has-error={!!error}>
        <slot id={resolvedId} {describedBy} />
    </div>

    {#if hintId}<span class="hint" id={hintId}>{hint}</span>{/if}
    {#if errorId}<span class="error" id={errorId} role="alert">{error}</span>{/if}
</div>
